import { BackHeader } from '@/design-system';
import { DIRECT_ENTRY_HISTORY_THRESHOLD } from '../constants/navigation';
import { useAuthCertificationRedirectStore } from '../store/authCertificationRedirectStore';
import { useUstoreDetailTitleStore } from '../store/ustoreDetailTitleStore';
import { cn } from '../utils/cn';
import { GNB_HEIGHT_CSS_VAR } from '../utils/common/gnbHeight';
import { Z_INDEX } from '../utils/common/zIndex';
import { useIsMajorPathname } from '../utils/hook/GNB';
import useBandBanner from '../utils/hook/useBandBanner';
import { useHistoryMethod } from '../utils/hook/useHistoryMethod';
import usePublishElementHeight from '../utils/hook/usePublishElementHeight';

import BandBanner from './BandBanner';
import NavbarCartButton from './NavbarCartButton';

/** 띠배너 노출·해제에 맞춰 헤더가 따라 움직이는 시간(ms) — AnimatedBannerWrapper의 높이 전환과 같은 값 */
const BANNER_TRANSITION_MS = 300;

/**
 * 배경·글자·아이콘 색 전환 클래스. 배너가 접히는 속도와 같이 움직여야 해서
 * duration은 위 BANNER_TRANSITION_MS와 같은 값으로 유지한다(한쪽만 바꾸면 따로 논다).
 */
const COLOR_TRANSITION_CLASS = 'transition-colors duration-300 ease-in-out';

const UstoreDetailBTB = () => {
  const ustoreDetailTitle = useUstoreDetailTitleStore.use.ustoreDetailTitle();
  const { isUstoreDetailPage, isUstoreOrderItemDetailPage } =
    useIsMajorPathname();
  const redirectUri = useAuthCertificationRedirectStore.use.redirectUri();
  const fallbackUri = useAuthCertificationRedirectStore.use.fallbackUri();
  const history = useHistoryMethod();
  const { bannerHeight, isScrollZero } = useBandBanner();

  /**
   * 상품상세 상단 크롬의 BTB 행 높이만 CSS 변수(GNB_HEIGHT_CSS_VAR)로 발행 — 상세 탭 sticky top 등이 구독.
   * 띠배너는 측정에서 제외해 발행값이 띠배너 노출 토글에 흔들리지 않도록 한다(안정 단일 inset).
   */
  const btbRowRef = usePublishElementHeight(GNB_HEIGHT_CSS_VAR, {
    skipZero: true,
  });

  /**
   * 모바일에서 인증 후 리다이렉션 된 다음 뒤로가기를 누르면 인증화면이 아닌 fallbackUri로 리다이렉션
   * 히스토리가 없으면(CRM 링크 등 직접 진입) /ustore로 이동
   *
   * @link https://www.notion.so/QA-2bf8574b70a58054adeadf42db31139a?source=copy_link#2c58574b70a580e2a295cc3f9af3f314
   */
  const onClickBackbutton = () => {
    if (redirectUri) {
      return history.replace(fallbackUri);
    }
    if (
      typeof window !== 'undefined' &&
      window.history.length <= DIRECT_ENTRY_HISTORY_THRESHOLD
    ) {
      return history.replace('/ustore');
    }
    history.back();
  };

  /**
   * 최상단에 있을 때만 배경을 비운다 — 단일상품·품목 상세 모두 첫 화면이 사진이다.
   * 판정은 띠배너와 같은 신호(isScrollZero)를 쓴다. 별도 스크롤 임계값을 두면
   * 배너는 이미 접혔는데 헤더만 한참 투명하게 남아 전환이 늦어 보인다.
   */
  const showBgTransparent =
    (isUstoreDetailPage || isUstoreOrderItemDetailPage) && isScrollZero;

  return (
    <>
      {/*
        첫구매(Better) 등 운영 띠배너 — BandBanner가 노출 조건(상품 상세 location)을 판단.
        상품 사진을 가리지 않도록 흐름 안 형제로 둬서 사진을 배너 높이만큼 아래로 밀어낸다.
        (헤더 안에 두면 사진 위를 덮어 썸네일 상단이 잘려 보인다)
      */}
      <BandBanner />
      {/* 헤더는 사진 위에 얹히는 투명 크롬이라 흐름 밖(fixed) — 띠배너 아래에서 시작한다 */}
      <div
        className={`fixed inset-x-0 ${showBgTransparent ? '' : 'bg-white'}`}
        style={{
          // 배경색은 인라인 transition으로 처리 — 배너 접힘(top)과 같은 시간에 끝나야 한다
          top: bannerHeight.mobile,
          transition: `top ${BANNER_TRANSITION_MS}ms ease-in-out, background-color ${BANNER_TRANSITION_MS}ms ease-in-out`,
          zIndex: Z_INDEX.USTORE_DETAIL_BTB,
        }}
      >
        {/* 발행 높이는 이 헤더 행만 측정 — 탭 sticky top이 안정적인 단일 값을 구독하도록 함 */}
        <div ref={btbRowRef}>
          <BackHeader
            title={
              <span
                className={cn(
                  COLOR_TRANSITION_CLASS,
                  showBgTransparent ? 'text-white' : 'text-gray-800',
                )}
              >
                {ustoreDetailTitle}
              </span>
            }
            onBack={onClickBackbutton}
            right={
              /* 뒤로가기 버튼은 자기 여백(8)으로 끝에서 떨어지는데, 장바구니 버튼은 그게 없어 직접 띄운다 */
              <div className="pr-16px">
                <NavbarCartButton
                  iconClassName={cn(
                    COLOR_TRANSITION_CLASS,
                    showBgTransparent ? 'stroke-white' : 'stroke-gray-800',
                  )}
                />
              </div>
            }
            className={cn(
              // 자리는 바깥 고정 상자가 정한다 — 헤더 기본 sticky를 끄지 않으면 GNB 높이만큼 더 내려간다
              'static',
              // 배경은 바깥 상자가 칠하고, 경계선은 사진 위에 줄을 그어 두지 않는다
              'bg-transparent border-b-0',
              // 뒤로가기 버튼은 자기 글자색을 갖고 있어 물려받지 않는다 — 첫째 자식인 그 버튼만 집어 바꾼다
              showBgTransparent && '[&>button:first-child]:text-white',
              COLOR_TRANSITION_CLASS,
            )}
          />
        </div>
      </div>
    </>
  );
};

export default UstoreDetailBTB;
